import { DateTime } from "luxon";
import { z } from "zod";
import { secondsSchema } from "../units/duration.js";
import { accountIdSchema } from "./account.js";
import { dateCustomFieldTypeSchema } from "./customField.js";
import { dateBasisSchema } from "./dateBasis.js";
import { defaultDateRange } from "./dateRange.js";
import { groupIdSchema } from "./group.js";
import { sentiments } from "./interactionMoment.js";
import { playbookIdSchema } from "./playbook.js";
import { metadataFieldIdSchema, signalIdSchema } from "./signal.js";
import { teammateIdSchema } from "./teammate.js";
import { topicIdSchema } from "./topic.js";
const isoDateTimeSchema = z.iso.datetime({ offset: true }).brand();
function asISODateTime(s) {
  return isoDateTimeSchema.parse(s);
}
function toISODateTime(dateTime) {
  return dateTime.toISO();
}
function resolveDefaultDateRange() {
  const before = (defaultDateRange.end ?? DateTime.now()).toUTC();
  return {
    after: toISODateTime(before.minus(defaultDateRange.length)),
    before: toISODateTime(before)
  };
}
const dateRangeSchema = z.object({
  after: isoDateTimeSchema,
  before: isoDateTimeSchema
});
const playbookScopeSchema = z.object({
  playbookId: playbookIdSchema,
  dateRange: dateRangeSchema,
  /** Defaults to `last_activity`; older clients omit it. */
  dateBasis: dateBasisSchema.optional(),
  /**
   * IANA time zone whose calendar days the date range should be aligned to
   * and metrics bucketed by (the viewer's browser zone). Only the API
   * boundary reads it, resolving the request's zone — this one when the
   * server knows it, else the company's — into the `timeZone` the queries
   * take; nothing below that boundary takes this type.
   */
  timeZone: z.string()
});
const valueMatchSchema = z.discriminatedUnion("op", [
  z.object({ op: z.literal("equals"), value: z.string() }),
  z.object({ op: z.literal("exists") })
]);
const customFieldMatchSchema = z.union([
  valueMatchSchema,
  z.object({
    op: z.enum(["=", "<", "<=", ">", ">="]),
    value: z.number()
  }),
  z.object({
    op: z.enum(["after", "before"]),
    fieldType: dateCustomFieldTypeSchema,
    value: z.iso.date()
  }),
  z.object({
    op: z.literal("within"),
    fieldType: dateCustomFieldTypeSchema,
    start: z.iso.date(),
    end: z.iso.date()
  }).refine(({ start, end }) => start <= end, {
    message: "Custom field date range start must not be after end"
  })
]);
const signalMetadataEnumEqualsFilterSchema = z.object({
  type: z.literal("enum"),
  op: z.literal("equals"),
  fieldId: metadataFieldIdSchema,
  value: z.string()
});
const signalMetadataNumericRangeFilterSchema = z.object({
  type: z.literal("number"),
  op: z.literal("range"),
  fieldId: metadataFieldIdSchema,
  lower: z.number(),
  upper: z.number()
});
const signalMetadataNumericCompareFilterSchema = z.object({
  type: z.literal("number"),
  op: z.enum(["<", "<=", ">", ">=", "="]),
  fieldId: metadataFieldIdSchema,
  value: z.number()
});
const signalMetadataFilterSchema = z.union([
  signalMetadataEnumEqualsFilterSchema,
  signalMetadataNumericRangeFilterSchema,
  signalMetadataNumericCompareFilterSchema
]);
const signalFilterSchema = z.object({
  type: z.literal("signal"),
  id: signalIdSchema,
  metadataFilters: z.array(signalMetadataFilterSchema).optional()
});
const teammateFilterSchema = z.object({
  type: z.literal("teammate"),
  ids: z.array(teammateIdSchema).min(1).readonly()
});
const teammateGroupFilterSchema = z.object({
  type: z.literal("teammate_group"),
  /** @deprecated Use `ids` instead. */
  id: groupIdSchema.optional(),
  /** The teammate must be in at least one of the provided groups. If an empty array is provided, the result set will be empty because no teammates will match. */
  ids: z.array(groupIdSchema).readonly().optional()
}).transform(
  ({ id, ids }) => {
    if (ids !== void 0) {
      return { type: "teammate_group", ids };
    }
    if (id !== void 0) {
      return { type: "teammate_group", ids: [id] };
    }
    return { type: "teammate_group", ids: [] };
  }
);
const gradingKeyFilterSchema = z.object({
  type: z.literal("grading_key"),
  keys: z.array(
    z.object({
      teammateId: teammateIdSchema,
      accountId: accountIdSchema,
      splitKey: z.uuid()
    })
  ).min(1).readonly()
});
function serializeGradingKey(key) {
  return `${key.teammateId}:${key.accountId}:${key.splitKey}`;
}
const customFieldFilterSchema = z.object({
  type: z.literal("custom_field"),
  id: z.uuid(),
  match: customFieldMatchSchema
});
const outcomeFilterSchema = z.object({
  type: z.literal("outcome"),
  id: z.string(),
  outcomeKind: z.enum(["preset", "custom"]),
  match: valueMatchSchema
});
const criteriaScoreFilterSchema = z.union([
  z.object({
    valueType: z.literal("1_5_score"),
    op: z.enum(["=", "<", "<=", ">", ">="]),
    value: z.number().int().min(1).max(5)
  }),
  z.object({
    valueType: z.literal("yes_no"),
    value: z.boolean()
  }),
  z.object({
    valueType: z.enum(["1_5_score", "yes_no"]),
    op: z.enum(["is_applicable", "is_not_applicable"])
  })
]);
function checkIsCriterionScoreApplicabilityFilter(score) {
  return "op" in score && (score.op === "is_applicable" || score.op === "is_not_applicable");
}
const criteriaFilterSchema = z.object({
  type: z.literal("criteria"),
  id: z.string(),
  score: criteriaScoreFilterSchema
});
const sectionFilterSchema = z.object({
  type: z.literal("section"),
  id: z.string(),
  notApplicable: z.boolean()
});
const playbookHasResultFilterSchema = z.object({
  type: z.literal("playbook_has_result")
});
const totalRecordingDurationFilterSchema = z.object({
  type: z.literal("total_recording_duration"),
  op: z.enum(["<", "<=", ">", ">="]),
  value: secondsSchema
});
const customerExperienceScoreFilterSchema = z.object({
  type: z.literal("customer_experience_score"),
  op: z.enum(["=", "<", "<=", ">", ">="]),
  value: z.number().int().min(1).max(5)
});
const recordingCountFilterSchema = z.object({
  type: z.literal("recording_count"),
  op: z.enum(["=", "<", "<=", ">", ">="]),
  value: z.number().int().nonnegative()
});
const firstCallDateFilterSchema = z.object({
  type: z.literal("first_call_date"),
  after: isoDateTimeSchema,
  before: isoDateTimeSchema
});
const ingestedDateFilterSchema = z.object({
  type: z.literal("ingested_date"),
  after: isoDateTimeSchema.optional(),
  before: isoDateTimeSchema.optional()
});
const coachingGoalFilterStatusSchema = z.enum([
  "goal_met",
  "near_goal",
  "on_track",
  "no_data",
  "behind"
]);
const coachingGoalFilterSchema = z.object({
  type: z.literal("coaching_goal"),
  status: coachingGoalFilterStatusSchema
});
const topicFilterSchema = z.object({
  type: z.literal("topic"),
  id: topicIdSchema
});
const sentimentFilterSchema = z.object({
  type: z.literal("sentiment"),
  value: z.enum(sentiments)
});
const signalsPageFilterSchema = z.discriminatedUnion("type", [
  signalFilterSchema,
  teammateGroupFilterSchema,
  customFieldFilterSchema,
  outcomeFilterSchema,
  criteriaFilterSchema,
  sectionFilterSchema,
  playbookHasResultFilterSchema,
  totalRecordingDurationFilterSchema,
  recordingCountFilterSchema,
  firstCallDateFilterSchema
]);
const topicsPageFilterSchema = z.discriminatedUnion("type", [
  teammateGroupFilterSchema,
  customFieldFilterSchema,
  outcomeFilterSchema,
  criteriaFilterSchema,
  sectionFilterSchema,
  playbookHasResultFilterSchema,
  totalRecordingDurationFilterSchema,
  recordingCountFilterSchema,
  firstCallDateFilterSchema
]);
const teamPerformancePageFilterSchema = z.discriminatedUnion("type", [
  signalFilterSchema,
  teammateGroupFilterSchema,
  customFieldFilterSchema,
  outcomeFilterSchema,
  criteriaFilterSchema,
  sectionFilterSchema,
  playbookHasResultFilterSchema,
  totalRecordingDurationFilterSchema,
  recordingCountFilterSchema,
  firstCallDateFilterSchema,
  coachingGoalFilterSchema
]);
function shiftDateRangeToPreviousPeriod(dateRange) {
  const { after, before } = dateRange;
  const afterDt = DateTime.fromISO(after, { zone: "utc" });
  const beforeDt = DateTime.fromISO(before, { zone: "utc" });
  if (!afterDt.isValid || !beforeDt.isValid) {
    throw new Error("shiftDateRangeToPreviousPeriod: invalid date range");
  }
  const length = beforeDt.toMillis() - afterDt.toMillis();
  const priorAfter = afterDt.minus({ milliseconds: length });
  const priorBefore = afterDt.minus({ milliseconds: 1 });
  return {
    after: toISODateTime(priorAfter.toUTC()),
    before: toISODateTime(priorBefore.toUTC())
  };
}
function stripSignalFilter(filters) {
  return filters.filter(
    (f) => f.type !== "signal"
  );
}
function stripOutcomeFilter(filters) {
  return filters.filter(
    (f) => f.type !== "outcome"
  );
}
function stripSentimentFilter(filters) {
  return filters.filter(
    (f) => f.type !== "sentiment"
  );
}
function selectPopulationFilters(filters) {
  return filters.filter(
    (f) => f.type === "playbook_has_result"
  );
}
export {
  asISODateTime,
  checkIsCriterionScoreApplicabilityFilter,
  coachingGoalFilterSchema,
  coachingGoalFilterStatusSchema,
  criteriaFilterSchema,
  criteriaScoreFilterSchema,
  customFieldFilterSchema,
  customFieldMatchSchema,
  customerExperienceScoreFilterSchema,
  dateRangeSchema,
  firstCallDateFilterSchema,
  gradingKeyFilterSchema,
  ingestedDateFilterSchema,
  isoDateTimeSchema,
  outcomeFilterSchema,
  playbookHasResultFilterSchema,
  playbookScopeSchema,
  recordingCountFilterSchema,
  resolveDefaultDateRange,
  sectionFilterSchema,
  selectPopulationFilters,
  sentimentFilterSchema,
  serializeGradingKey,
  shiftDateRangeToPreviousPeriod,
  signalFilterSchema,
  signalMetadataFilterSchema,
  signalsPageFilterSchema,
  stripOutcomeFilter,
  stripSentimentFilter,
  stripSignalFilter,
  teamPerformancePageFilterSchema,
  teammateFilterSchema,
  toISODateTime,
  topicsPageFilterSchema,
  totalRecordingDurationFilterSchema,
  valueMatchSchema
};
//# sourceMappingURL=filters.js.map
